import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Cpu,
  DollarSign,
  Loader2,
  Rocket,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";

const ACCENT = "#00D4FF";
const GOLD = "#D4AF37";
const MATTE = "#0A0A0F";

const LEARN_POINTS = [
  {
    icon: DollarSign,
    text: "Where your $4,200/month revenue leak is hiding",
  },
  {
    icon: Cpu,
    text: "The 5 automation pillars dealerships use against you",
  },
  {
    icon: Rocket,
    text: "The 30-day deployment playbook (45 min total setup)",
  },
  {
    icon: TrendingUp,
    text: "Real case study: 12 cars/week → 22 in 90 days",
  },
];

// Trigger a native download without navigating away. Uses a hidden anchor
// because window.open on mobile Safari often opens the PDF in a new tab
// rather than saving it — the `download` attr is the reliable signal.
function triggerDownload(url, filename = "mechanics-advantage-guide.pdf") {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export default function Guide() {
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [downloaded, setDownloaded] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");

  const canSubmit =
    businessName.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
    !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/guide/lead", {
        business_name: businessName.trim(),
        email: email.trim(),
      });
      if (data?.success && data?.download_url) {
        setDownloadUrl(data.download_url);
        triggerDownload(data.download_url);
        setDownloaded(true);
      } else {
        setError("Something went sideways. Try again in a moment.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail[0]?.msg
            : "Submission failed. Check your email format and try again.";
      setError(msg || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen text-white font-inter relative overflow-hidden"
      style={{ background: MATTE }}
      data-testid="guide-page"
    >
      {/* Circuit-board glow backdrop */}
      <CircuitBackdrop />

      <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 pt-8 pb-24">
        {/* Compact header */}
        <div className="flex items-center justify-between mb-14 sm:mb-20">
          <Link
            to="/"
            className="text-[10px] sm:text-[11px] uppercase tracking-[0.32em] text-slate-400 hover:text-white transition-colors"
            data-testid="guide-back-home"
          >
            ← Wingman Alfred
          </Link>
          <div
            className="flex items-center gap-1.5 text-[9px] sm:text-[10px] uppercase tracking-[0.28em]"
            style={{ color: ACCENT }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full light-pulse"
              style={{ backgroundColor: ACCENT }}
            />
            Field Intelligence
          </div>
        </div>

        {/* Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-12 lg:gap-16 items-center">
          <div>
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] sm:text-[11px] uppercase tracking-[0.28em] mb-6"
              style={{
                background: "rgba(0,212,255,0.08)",
                border: `1px solid ${ACCENT}55`,
                color: ACCENT,
              }}
              data-testid="guide-eyebrow"
            >
              <ShieldCheck size={12} />
              Free Intelligence Report
            </div>

            <h1
              className="font-orbitron text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-white mb-5"
              data-testid="guide-headline"
            >
              The Independent Mechanic&apos;s{" "}
              <span style={{ color: ACCENT }}>New Advantage</span>
            </h1>

            <p
              className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl"
              data-testid="guide-subheadline"
            >
              How AI-Powered Operations Are Giving Small Shops the Edge Over
              Corporate Dealerships.
            </p>
          </div>

          <BookCover />
        </div>

        {/* What You'll Learn */}
        <section className="mt-20 sm:mt-24" data-testid="guide-learn-section">
          <div className="flex items-center gap-3 mb-8">
            <span
              className="h-px flex-1 max-w-[80px]"
              style={{
                background: `linear-gradient(90deg, ${ACCENT}77, transparent)`,
              }}
            />
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.34em] text-slate-400">
              What you&apos;ll learn
            </div>
            <span
              className="h-px flex-1"
              style={{
                background: `linear-gradient(90deg, transparent, ${ACCENT}22)`,
              }}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {LEARN_POINTS.map(({ icon: Icon, text }, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-4 sm:p-5 rounded-md border transition-colors"
                style={{
                  borderColor: "rgba(255,255,255,0.06)",
                  background: "rgba(0,212,255,0.02)",
                }}
                data-testid={`guide-learn-point-${i}`}
              >
                <div
                  className="w-9 h-9 shrink-0 rounded-md inline-flex items-center justify-center"
                  style={{
                    background: "rgba(0,212,255,0.10)",
                    border: `1px solid ${ACCENT}44`,
                    color: ACCENT,
                  }}
                >
                  <Icon size={16} />
                </div>
                <div className="text-sm sm:text-[15px] text-slate-200 leading-relaxed pt-1.5">
                  {text}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Capture form OR Post-Download CTA */}
        <section
          id="capture"
          className="mt-16 sm:mt-24 flex justify-center"
          data-testid="guide-capture-section"
        >
          {!downloaded ? (
            <CaptureForm
              businessName={businessName}
              setBusinessName={setBusinessName}
              email={email}
              setEmail={setEmail}
              canSubmit={canSubmit}
              submitting={submitting}
              error={error}
              onSubmit={handleSubmit}
            />
          ) : (
            <TacticalMoveCard downloadUrl={downloadUrl} />
          )}
        </section>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- Backdrop --- */

function CircuitBackdrop() {
  return (
    <div className="absolute inset-0 pointer-events-none">
      {/* Radial glow */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 20% 20%, rgba(0,212,255,0.10) 0%, transparent 55%), radial-gradient(ellipse at 80% 90%, rgba(0,212,255,0.06) 0%, transparent 55%)",
        }}
      />
      {/* Circuit grid */}
      <div
        className="absolute inset-0 opacity-[0.10]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,212,255,0.55) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.55) 1px, transparent 1px)",
          backgroundSize: "68px 68px",
          maskImage:
            "radial-gradient(ellipse at 50% 50%, black 40%, transparent 85%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 50% 50%, black 40%, transparent 85%)",
        }}
      />
      {/* Diagonal trace lines */}
      <svg
        className="absolute inset-0 w-full h-full opacity-[0.07]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern
            id="circuit"
            x="0"
            y="0"
            width="240"
            height="240"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M0 60 L120 60 L120 180 L240 180"
              stroke="#00D4FF"
              strokeWidth="1"
              fill="none"
            />
            <circle cx="120" cy="60" r="2.5" fill="#00D4FF" />
            <circle cx="120" cy="180" r="2.5" fill="#00D4FF" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#circuit)" />
      </svg>
    </div>
  );
}

/* -------------------------------------------------------------- Book cover --- */

function BookCover() {
  return (
    <div
      className="relative mx-auto lg:mx-0 w-full max-w-[300px] aspect-[3/4] rounded-md p-6 sm:p-7 flex flex-col justify-between"
      style={{
        background:
          "linear-gradient(155deg, #0F0F18 0%, #060610 60%, #0A0A0F 100%)",
        border: `1px solid ${ACCENT}55`,
        boxShadow:
          "0 0 0 1px rgba(0,212,255,0.10) inset, 0 25px 60px -20px rgba(0,212,255,0.35), 0 8px 20px -8px rgba(0,0,0,0.8)",
      }}
      data-testid="guide-book-cover"
    >
      <div
        className="absolute inset-0 rounded-md pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 30% 20%, rgba(0,212,255,0.14) 0%, transparent 55%)",
        }}
      />

      <div className="relative">
        <div
          className="text-[9px] uppercase tracking-[0.4em] mb-4"
          style={{ color: ACCENT }}
        >
          Field Guide · 2026
        </div>
        <div className="font-orbitron text-lg sm:text-xl font-bold leading-tight text-white">
          The Independent Mechanic&apos;s
        </div>
        <div
          className="font-orbitron text-2xl sm:text-3xl font-black leading-tight mt-1"
          style={{ color: ACCENT }}
        >
          New Advantage
        </div>
      </div>

      <div className="relative">
        <div
          className="h-px w-full my-3"
          style={{
            background: `linear-gradient(90deg, ${ACCENT}, transparent)`,
          }}
        />
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-slate-400">
          <BookOpen size={11} style={{ color: ACCENT }} />
          Wingman Alfred
        </div>
      </div>

      {/* Corner accents */}
      {["top-2 left-2", "top-2 right-2", "bottom-2 left-2", "bottom-2 right-2"].map(
        (pos) => (
          <span
            key={pos}
            className={`absolute ${pos} w-2 h-2 border rounded-full`}
            style={{ borderColor: `${ACCENT}88` }}
          />
        )
      )}
    </div>
  );
}

/* --------------------------------------------------------------- Capture --- */

function CaptureForm({
  businessName,
  setBusinessName,
  email,
  setEmail,
  canSubmit,
  submitting,
  error,
  onSubmit,
}) {
  return (
    <form
      onSubmit={onSubmit}
      className="w-full max-w-md p-6 sm:p-8 rounded-lg relative"
      style={{
        background: "rgba(16,16,24,0.75)",
        border: `1px solid ${ACCENT}55`,
        backdropFilter: "blur(12px)",
        boxShadow: `0 0 40px -10px ${ACCENT}44, 0 20px 40px -20px #000`,
      }}
      data-testid="guide-form"
    >
      <div
        className="absolute inset-x-0 top-0 h-0.5"
        style={{
          background: `linear-gradient(90deg, transparent, ${ACCENT}, transparent)`,
        }}
      />

      <div className="text-center mb-6">
        <div
          className="text-[10px] uppercase tracking-[0.3em] mb-2"
          style={{ color: ACCENT }}
        >
          Unlock the Guide
        </div>
        <div className="font-orbitron text-xl sm:text-2xl font-bold text-white">
          Instant download
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label
            htmlFor="guide-business"
            className="block text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2"
          >
            Business Name
          </label>
          <input
            id="guide-business"
            type="text"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder="Waldo Tire & Automotive"
            maxLength={80}
            autoCapitalize="words"
            data-testid="guide-input-business"
            className="w-full bg-[#060610] border rounded-md px-4 py-3 text-white text-base focus:outline-none transition-colors"
            style={{ borderColor: "rgba(255,255,255,0.10)" }}
            onFocus={(e) => (e.target.style.borderColor = ACCENT)}
            onBlur={(e) =>
              (e.target.style.borderColor = "rgba(255,255,255,0.10)")
            }
          />
        </div>
        <div>
          <label
            htmlFor="guide-email"
            className="block text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2"
          >
            Email
          </label>
          <input
            id="guide-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yourshop.com"
            autoCapitalize="off"
            autoCorrect="off"
            data-testid="guide-input-email"
            className="w-full bg-[#060610] border rounded-md px-4 py-3 text-white text-base focus:outline-none transition-colors"
            style={{ borderColor: "rgba(255,255,255,0.10)" }}
            onFocus={(e) => (e.target.style.borderColor = ACCENT)}
            onBlur={(e) =>
              (e.target.style.borderColor = "rgba(255,255,255,0.10)")
            }
          />
        </div>
      </div>

      {error && (
        <div
          className="mt-4 text-[11px] uppercase tracking-[0.2em] text-red-400 text-center"
          data-testid="guide-form-error"
          role="alert"
        >
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!canSubmit}
        data-testid="guide-submit"
        className="w-full mt-6 py-4 rounded-md font-orbitron text-sm font-bold uppercase tracking-[0.24em] inline-flex items-center justify-center gap-2 transition-transform"
        style={{
          background: canSubmit
            ? `linear-gradient(90deg, ${ACCENT}, #0091EA)`
            : "rgba(255,255,255,0.06)",
          color: canSubmit ? MATTE : "#64748B",
          boxShadow: canSubmit ? `0 12px 30px -12px ${ACCENT}88` : "none",
          cursor: canSubmit ? "pointer" : "not-allowed",
        }}
      >
        {submitting ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Sending
          </>
        ) : (
          <>
            <BookOpen size={15} /> Unlock the Guide
          </>
        )}
      </button>

      <div className="mt-4 text-center text-[10px] uppercase tracking-[0.25em] text-slate-500">
        Free · Instant download · No credit card
      </div>
    </form>
  );
}

/* --------------------------------------------------- Post-Download CTA --- */

function TacticalMoveCard({ downloadUrl }) {
  return (
    <div
      className="w-full max-w-md p-6 sm:p-8 rounded-lg relative text-center"
      style={{
        background: "rgba(20,16,8,0.85)",
        border: `1px solid ${GOLD}66`,
        backdropFilter: "blur(12px)",
        boxShadow: `0 0 44px -10px ${GOLD}55, 0 20px 40px -20px #000`,
      }}
      data-testid="guide-tactical-card"
    >
      <div
        className="absolute inset-x-0 top-0 h-0.5"
        style={{
          background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
        }}
      />

      <div
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.28em] mb-4"
        style={{
          background: "rgba(212,175,55,0.10)",
          border: `1px solid ${GOLD}55`,
          color: GOLD,
        }}
      >
        <CheckCircle2 size={12} />
        Guide Delivered
      </div>

      <div className="font-orbitron text-xl sm:text-2xl font-bold text-white mb-3">
        Next Tactical Move
      </div>
      <p className="text-sm sm:text-[15px] text-slate-300 leading-relaxed mb-6">
        Your guide is downloading. While you read it — want to see exactly how
        much revenue <b>your shop</b> is leaking?
      </p>

      <Link
        to="/local"
        data-testid="guide-cta-assessment"
        className="w-full py-4 rounded-md font-orbitron text-sm font-bold uppercase tracking-[0.22em] inline-flex items-center justify-center gap-2 transition-transform hover:translate-y-[-1px]"
        style={{
          background: `linear-gradient(90deg, ${GOLD}, #B8912C)`,
          color: MATTE,
          boxShadow: `0 12px 30px -12px ${GOLD}88`,
        }}
      >
        Take the 3-Minute Assessment <ArrowRight size={15} />
      </Link>

      {downloadUrl && (
        <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-slate-500">
          Didn&apos;t download?{" "}
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="guide-manual-download"
            className="underline hover:text-white"
            style={{ color: GOLD }}
          >
            Grab it here
          </a>
        </div>
      )}
    </div>
  );
}
